import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { profileAPI, paymentsAPI, masterclassesAPI, mentorsAPI } from '../services/api';
import Avatar from '../components/Avatar';
import './Profile.css';

const TABS = [
  { id: 'profil', label: 'Mon profil' },
  { id: 'formations', label: 'Mes formations' },
  { id: 'sessions', label: 'Mes sessions' },
  { id: 'abonnement', label: 'Abonnement' },
];

export default function Profile() {
  const { user, refreshUser, logout } = useAuth();
  const [tab, setTab] = useState('profil');
  const [profileData, setProfileData] = useState(null);
  const [enrollments, setEnrollments] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [subscription, setSubscription] = useState(null);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState('');
  const [form, setForm] = useState({});

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!user) return;
    loadTabData(tab);
  }, [tab, user]);

  const loadTabData = async (t) => {
    try {
      if (t === 'profil' && !profileData) {
        const { data } = await profileAPI.show();
        setProfileData(data.user);
        setForm({ first_name: data.user.first_name, last_name: data.user.last_name, phone: data.user.phone || '', city: data.user.city || '', wilaya: data.user.wilaya || '', bio: data.user.bio || '', domain: data.user.domain || '' });
      }
      if (t === 'formations' && enrollments.length === 0) {
        const { data } = await masterclassesAPI.myEnrollments();
        setEnrollments(data.data);
      }
      if (t === 'sessions' && sessions.length === 0) {
        const { data } = await mentorsAPI.mySessions();
        setSessions(data.data);
      }
      if (t === 'abonnement' && !subscription) {
        const { data } = await paymentsAPI.activeSubscription();
        setSubscription(data.subscription);
      }
    } catch {}
  };

  const saveProfile = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await profileAPI.update(form);
      await refreshUser();
      showToast('Profil mis à jour !');
    } catch (err) {
      showToast(err.response?.data?.message || 'Erreur lors de la sauvegarde.');
    } finally { setSaving(false); }
  };

  const showToast = (msg) => { setToast(msg); setTimeout(() => setToast(''), 4000); };

  if (!user) {
    return (
      <div className="profile-page">
        <div className="not-connected">
          <p>Vous devez être connecté pour accéder à votre profil.</p>
          <a href="/login" className="auth-link">Se connecter</a>
        </div>
      </div>
    );
  }

  return (
    <div className="profile-page">
      <div className="profile-header">
        <Avatar user={user} size="lg" className="profile-avatar" />
        <div>
          <h1>{user.full_name}</h1>
          <p>{user.email} · <span className="role-badge">{user.role}</span></p>
        </div>
      </div>

      <div className="profile-tabs">
        {TABS.map((t) => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            className={`tab-btn ${tab === t.id ? 'active' : ''}`}
          >{t.label}</button>
        ))}
      </div>

      <div className="profile-body">
        {tab === 'profil' && (
          <form onSubmit={saveProfile} className="profile-form">
            <div className="form-row-2">
              <div>
                <label>Prénom</label>
                <input value={form.first_name || ''} onChange={(e) => setForm({ ...form, first_name: e.target.value })} />
              </div>
              <div>
                <label>Nom</label>
                <input value={form.last_name || ''} onChange={(e) => setForm({ ...form, last_name: e.target.value })} />
              </div>
            </div>
            <label>Téléphone</label>
            <input value={form.phone || ''} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="+213..." />
            <label>Ville</label>
            <input value={form.city || ''} onChange={(e) => setForm({ ...form, city: e.target.value })} />
            <label>Domaine d'activité</label>
            <input value={form.domain || ''} onChange={(e) => setForm({ ...form, domain: e.target.value })} placeholder="Ex : Finance, Tech, Marketing..." />
            <label>Bio</label>
            <textarea rows={4} value={form.bio || ''} onChange={(e) => setForm({ ...form, bio: e.target.value })} placeholder="Quelques mots sur vous..." />
            <button type="submit" className="save-btn" disabled={saving}>
              {saving ? 'Sauvegarde...' : 'Enregistrer'}
            </button>
          </form>
        )}

        {tab === 'formations' && (
          <div className="tab-content">
            {enrollments.length === 0 ? (
              <p className="empty-state">Aucune formation en cours. <a href="/masterclasses">Explorer les masterclasses</a></p>
            ) : enrollments.map((e) => (
              <div key={e.id} className="list-item">
                <div>
                  <strong>{e.masterclass?.title}</strong>
                  <p>{e.masterclass?.mentor?.user?.full_name}</p>
                </div>
                <span className="progress-badge">{e.progress_percent}% complété</span>
              </div>
            ))}
          </div>
        )}

        {tab === 'sessions' && (
          <div className="tab-content">
            {sessions.length === 0 ? (
              <p className="empty-state">Aucune session de mentorat. <a href="/mentors">Trouver un mentor</a></p>
            ) : sessions.map((s) => (
              <div key={s.id} className="list-item">
                <div>
                  <strong>{s.topic}</strong>
                  <p>Mentor : {s.mentor?.user?.full_name}</p>
                </div>
                <span className={`status-badge status-${s.status}`}>{s.status}</span>
              </div>
            ))}
          </div>
        )}

        {tab === 'abonnement' && (
          <div className="tab-content">
            {subscription ? (
              <div className="subscription-card">
                <div className="sub-icon">✅</div>
                <h3>Abonnement actif : {subscription.plan}</h3>
                <p>Payé le {subscription.paid_at}</p>
                <p className="sub-ref">Réf. {subscription.reference}</p>
              </div>
            ) : (
              <div className="no-sub">
                <p>Aucun abonnement actif.</p>
                <a href="/tarifs" className="sub-link">Voir nos plans</a>
              </div>
            )}
          </div>
        )}
      </div>

      {toast && <div className="toast-profile">{toast}</div>}

      <button className="logout-btn" onClick={logout}>Se déconnecter</button>
    </div>
  );
}
